import { useCallback, useId, useRef, useState } from 'react'
import { useTranslation } from '@mc/i18n'
import { Container } from '../Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '../i18n'
import { CarouselHeader } from '../shared/CarouselHeader'
import { useRowScroll } from '../shared/useRowScroll'
import type { InstructorsSectionData } from '../types/sections'
import {
  InstructorsCarousel,
  type InstructorsCarouselHandle,
  type InstructorsCarouselNavState,
} from './InstructorsCarousel'
import { INSTRUCTOR_IMAGES } from './constants/instructorImages'
import type { InstructorItem } from './types'

interface InstructorCopy {
  id: string
  name: string
  description: string
}

interface InstructorsSectionProps {
  data: InstructorsSectionData
}

export const InstructorsSection = ({ data }: InstructorsSectionProps) => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const carouselRef = useRef<InstructorsCarouselHandle>(null)
  const carouselId = useId()
  const { scrollRow } = useRowScroll(carouselRef)
  // Optimistic seed for the overflowing case, so the arrow does not flash
  // disabled before layout is measured. The carousel always emits its real
  // state on mount, so a non-overflowing rail corrects this rather than
  // leaving an enabled arrow that does nothing.
  const [navState, setNavState] = useState<InstructorsCarouselNavState>({
    canGoPrev: false,
    canGoNext: true,
  })

  const handleNavStateChange = useCallback(
    (nextNavState: InstructorsCarouselNavState) => setNavState(nextNavState),
    [],
  )

  const rawCopy = t(data.itemsKey, { returnObjects: true })
  const copy = Array.isArray(rawCopy) ? (rawCopy as InstructorCopy[]) : []
  const instructors: InstructorItem[] = copy.flatMap((item) => {
    const imageUrl = INSTRUCTOR_IMAGES[item.id]
    return imageUrl ? [{ ...item, imageUrl }] : []
  })

  if (instructors.length === 0) return null

  const headline = t('instructors.headline')

  return (
    <section
      id='instructors'
      data-section='instructors'
      className='w-full py-6 text-edu-fg-primary md:py-12'
    >
      <Container>
        <div className='flex flex-col gap-6 md:gap-4'>
          <CarouselHeader
            title={headline}
            prevLabel={t('instructors.previousLabel')}
            nextLabel={t('instructors.nextLabel')}
            onNav={scrollRow}
            controlsId={carouselId}
            canGoPrev={navState.canGoPrev}
            canGoNext={navState.canGoNext}
          />

          <InstructorsCarousel
            ref={carouselRef}
            id={carouselId}
            items={instructors}
            sectionLabel={t('instructors.sectionLabel')}
            onNavStateChange={handleNavStateChange}
          />
        </div>
      </Container>
    </section>
  )
}
